import { useTranslation } from 'next-i18next'
import React from 'react'
import { applyAutoPrefix, formatPhoneInput } from 'src/hooks/usePhoneInput'
import { ICoordinates, IDirectoryContact } from 'src/types/graphql'
import {
  DirectoryContactChannel,
  trackDirectoryContactClick,
  trackDirectoryWebsiteClick
} from 'src/utils/pixels/directoryEvents'

interface ContactResultProps {
  bundle: IDirectoryContact
  locationId: string
  /** Distinct reveals still available this month, or null when unknown. */
  remaining: number | null
  /** The facility's pin — powers the "Cómo llegar" directions row. */
  coordinates?: ICoordinates | null
  /**
   * Rendered for a SpotMe admin, whose reveal was read-only. Swaps the two
   * renter-framed footnotes: the "menciona que lo viste en SpotMe" nudge makes
   * no sense internally, and `remaining` must be read as "what a renter has
   * left" — not "what you have left", which would imply this lookup spent one.
   */
  isStaff?: boolean
}

const WHATSAPP_BASE = 'https://wa.me/'
const MX_COUNTRY_CODE = '52'

/**
 * A number we can confidently present as Mexican: 10 national digits, or 12
 * that already carry the 52. Anything else (7/8-digit legacy locals, numbers
 * with extensions) is shown exactly as the source printed it — guessing a
 * prefix onto those would manufacture a wrong number.
 */
function isMexicanDialable(digits: string): boolean {
  return (
    digits.length === 10 ||
    (digits.length === 12 && digits.startsWith(MX_COUNTRY_CODE))
  )
}

/**
 * The revealed contact bundle. Rendered only after a successful reveal, so it
 * never appears in an SSR payload — the bundle is not a field on `Location`
 * and is returned exclusively by `revealDirectoryContact`.
 *
 * The website deliberately lives INSIDE this gated bundle (settled 2026-08-23):
 * the operator's own site carries their phone number, so ungating it would
 * ungate the contact and make the monthly cap decorative.
 *
 * 2026-08-28 (Jules): Mexican numbers render in full international form
 * (+52 …, via the sanctioned `formatPhoneInput` — never a hand-rolled prefix),
 * a phone-only bundle gets a "try WhatsApp" row (17 of the first 18 reveals
 * chose WhatsApp as their own channel; DENUE only ever carries a phone, and
 * since 2019 MX mobiles and landlines share the 10-digit shape so we cannot
 * know which numbers have WhatsApp — the hint says so), and the pin becomes a
 * directions link inside the gated bundle.
 *
 * Every outbound link is `rel="nofollow noopener noreferrer"` — these are
 * unclaimed third-party businesses, and passing them link equity is not
 * something an unverified directory entry has earned.
 */
const ContactResult = ({
  bundle,
  locationId,
  remaining,
  coordinates,
  isStaff = false
}: ContactResultProps) => {
  const { t } = useTranslation('directorio')
  const { phone, whatsapp, email, website } = bundle

  const phoneDigits = (phone ?? '').replace(/\D/g, '')
  const phoneIsMexican = isMexicanDialable(phoneDigits)
  const phoneDisplay = phoneIsMexican ? formatPhoneInput(phoneDigits) : phone
  const phoneHref = phoneIsMexican
    ? `tel:${applyAutoPrefix(phoneDigits)}`
    : `tel:${(phone ?? '').replace(/[^\d+]/g, '')}`

  const rows: {
    key: DirectoryContactChannel
    label: string
    href: string
    value: string
    hint?: string
    external?: boolean
  }[] = []
  if (whatsapp) {
    rows.push({
      key: 'whatsapp',
      label: t('REVEAL.RESULT_WHATSAPP'),
      href: `${WHATSAPP_BASE}${whatsapp.replace(/\D/g, '')}`,
      value: whatsapp,
      external: true
    })
  }
  if (phone) {
    rows.push({
      key: 'phone',
      label: t('REVEAL.RESULT_PHONE'),
      href: phoneHref,
      value: phoneDisplay ?? phone
    })
  }
  // No WhatsApp on record but a Mexican mobile-shaped number: offer the chat
  // anyway. A miss costs the renter one tap ("este número no tiene WhatsApp").
  if (!whatsapp && phone && phoneIsMexican) {
    rows.push({
      key: 'whatsapp_try',
      label: t('REVEAL.RESULT_WHATSAPP'),
      href: `${WHATSAPP_BASE}${applyAutoPrefix(phoneDigits).replace(/\D/g, '')}`,
      value: t('REVEAL.WHATSAPP_TRY_CTA'),
      hint: t('REVEAL.WHATSAPP_TRY_HINT'),
      external: true
    })
  }
  if (email) {
    rows.push({
      key: 'email',
      label: t('REVEAL.RESULT_EMAIL'),
      href: `mailto:${email}`,
      value: email
    })
  }
  if (coordinates) {
    rows.push({
      key: 'directions',
      label: t('REVEAL.RESULT_DIRECTIONS'),
      href: `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`,
      value: t('REVEAL.DIRECTIONS_CTA'),
      external: true
    })
  }

  return (
    <section
      className="rounded-2xl border border-blue-500/25 bg-blue-500/5 p-5 md:p-6"
      data-testid="directory-reveal-result"
    >
      <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
        {t('REVEAL.RESULT_TITLE')}
      </h2>

      <dl className="mt-4 flex flex-col gap-3">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3"
          >
            <dt className="shrink-0 text-[11px] tracking-[0.04em] text-ink-200 uppercase md:w-24">
              {row.label}
            </dt>
            <dd className="min-w-0">
              <a
                href={row.href}
                target={row.external ? '_blank' : undefined}
                rel="nofollow noopener noreferrer"
                onClick={() =>
                  trackDirectoryContactClick({ locationId, channel: row.key })
                }
                className="text-[15px] font-medium break-words text-blue-800 underline underline-offset-2"
              >
                {row.value}
              </a>
              {row.hint && (
                <span className="ml-2 text-[12px] text-ink-200">
                  {row.hint}
                </span>
              )}
            </dd>
          </div>
        ))}
        {website && (
          <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
            <dt className="shrink-0 text-[11px] tracking-[0.04em] text-ink-200 uppercase md:w-24">
              {t('REVEAL.RESULT_WEBSITE')}
            </dt>
            <dd className="min-w-0">
              <a
                href={website}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() => trackDirectoryWebsiteClick(locationId)}
                className="text-[15px] font-medium break-words text-blue-800 underline underline-offset-2"
              >
                {website}
              </a>
            </dd>
          </div>
        )}
      </dl>

      <p className="mt-4 border-t border-blue-500/15 pt-3 text-[12px] leading-snug text-ink-200">
        {isStaff ? t('REVEAL.STAFF_NOTE') : t('REVEAL.RESULT_NOTE')}
      </p>
      {typeof remaining === 'number' && (
        <p className="mt-1.5 text-[11px] text-ink-200">
          {isStaff
            ? t('REVEAL.STAFF_REMAINING', { count: remaining })
            : remaining === 1
              ? t('REVEAL.REMAINING_ONE')
              : t('REVEAL.REMAINING_OTHER', { count: remaining })}
        </p>
      )}
    </section>
  )
}

export default React.memo(ContactResult)
